import client from '@config/client'
import API_URL from '@config/services'
import { ApiClientResult } from '@/types/api'

interface TenantPayload {
  name?: string
  email?: string
  phone?: string
  address?: string
  project_id?: number | string
  [key: string]: any
}

/**
 * Creates a new tenant
 * @param payload - Tenant data
 * @returns API response
 */
export const onCreateTenant = (
  payload: TenantPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.TENANTS.POST_TENANT(), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Deletes a tenant
 * @param id - Tenant ID
 * @returns API response
 */
export const onDeleteTenant = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.TENANTS.DELETE_TENANT_BY_ID(id))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Updates a tenant
 * @param id - Tenant ID
 * @param payload - Tenant data to update
 * @returns API response
 */
export const onUpdateTenant = (
  id: number | string,
  payload: TenantPayload
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.TENANTS.PATCH_TENANT_BY_ID(id), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets a tenant by ID
 * @param id - Tenant ID
 * @returns Tenant data
 */
export const onGetTenant = (id: number | string): Promise<any> => {
  return client
    .get(API_URL.TENANTS.GET_TENANT_BY_ID(id))
    .then(response => response.data)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets all tenants
 * @returns API response with tenants list
 */
export const onGetAllTenants = (): Promise<ApiClientResult> => {
  return client
    .get(API_URL.TENANTS.GET_TENANTS_LIST())
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets tenants by operation/project
 * @param operationId - Operation/Project ID
 * @returns API response with tenants list
 */
export const onGetTenantsByOperation = (
  operationId: number | string
): Promise<ApiClientResult> => {
  return client
    .get(API_URL.TENANTS.GET_TENANTS_LIST_BY_PROJECT(operationId))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Adds a file to a tenant
 * @param id - Tenant ID
 * @param fileId - File ID to add
 * @param files - Existing files array
 * @returns API response
 */
export const addTenantFile = (
  id: number | string,
  fileId: number | string,
  files?: (number | string)[]
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.TENANTS.PATCH_TENANT_FILE(id), {
      files: files ? [...files, fileId] : [fileId]
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Enables or disables portal access for an entity
 * @param id - Entity ID
 * @param type - Entity type
 * @param value - Enable/disable value
 * @returns API response
 */
export const onEnablePortal = (
  id: number | string,
  type: string,
  value: boolean
): Promise<ApiClientResult> => {
  return client
    .patch(`/${type}s/${id}/`, {
      portal_enabled: value
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets tenant invoices
 * @param id - Tenant ID
 * @returns API response with invoices list
 */
export const onGetTenantInvoices = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .get(API_URL.TENANTS.GET_TENANT_INVOICES(id))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}
